import {
  faArrowRight,
  faCheck,
  faSparkles,
  faXmark,
} from "@fortawesome/pro-regular-svg-icons";
import {
  Badge,
  Button,
  Icon,
  Input,
  Message,
  RadioCard,
  TextArea,
} from "@intility/bifrost-react";
import Select from "@intility/bifrost-react-select";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useCreateClient } from "../api/clients";
import { useAddClientToPlatform } from "../api/platforms";
import type { AppType, AuthMethod, ClientListItem } from "../api/types";
import { APP_TYPE_NAME_SUFFIX, slugify } from "../lib/clientTypes";
import { ClientStackPicker } from "./ClientStackPicker";
import { clientTypeState } from "./ClientTypeBadge";
import { NewClientCredentialQuestion } from "./NewClientCredentialQuestion";
import "./ClientObject.css";
import "./NewClientForm.css";

// The types offered when creating a client, in the order of the design.
const TYPE_CARDS: { type: AppType; label: string; description: string }[] = [
  {
    type: "spa",
    label: "SPA",
    description: "Single-Page Application. The browser window users log into.",
  },
  {
    type: "api",
    label: "API",
    description:
      "Backend API. Validates incoming tokens and can call other services.",
  },
  {
    type: "worker",
    label: "Other Service",
    description:
      "Outbound-only workload: backend job, integration, MCP server or agent.",
  },
];

// The purposes, each with the client types it works for (the purpose map). The
// first type of a purpose is the one picked when the purpose is chosen. The
// values and labels are the purposes of lib/clientTypes. An SPA has a second
// stored purpose, swagger-app, that only "Auto create" makes: both read as
// "SPA", so the Select offers one.
const PURPOSES: { value: string; label: string; types: AppType[] }[] = [
  { value: "web-app", label: "SPA", types: ["spa"] },
  { value: "api", label: "API", types: ["api"] },
  { value: "mcp-server", label: "MCP", types: ["api", "worker"] },
  { value: "ai-agent", label: "AI Agent", types: ["api", "worker"] },
  { value: "worker", label: "Worker", types: ["worker"] },
];

// What "Auto create" creates straight away, in order. Each is named after the
// platform and the part it plays ("Apm Frontend"). The API is not here: the
// registry requires an authMethod (SPIFFE or secret) for it, and where the
// workload runs is not known yet, so it is a draft the user completes (see
// NewApiClientForm).
const AUTO_CREATE_CLIENTS: {
  label: string;
  nameSuffix: string;
  appType: AppType;
  purposeTag: string;
}[] = [
  {
    label: "SPA",
    nameSuffix: "Frontend",
    appType: "spa",
    purposeTag: "web-app",
  },
  {
    label: "Swagger SPA",
    nameSuffix: "Swagger",
    appType: "spa",
    purposeTag: "swagger-app",
  },
];

// The form for a new client, inside the same box a client is shown in. With a
// platform the client is created in it; without one it is a standalone client.
export function NewClientForm({
  platformId,
  platformName,
  suggestAutoCreate = false,
  onAutoCreated,
  onCreated,
  onCancel,
}: {
  /** The platform to create the client in; none makes a standalone client. */
  platformId?: string;
  /** Names a client that is given no name of its own (with a platform). */
  platformName?: string;
  /** Offers "Auto create" under the heading, for a platform with no clients. */
  suggestAutoCreate?: boolean;
  /** Called once "Auto create" has made all its clients. */
  onAutoCreated?: () => void;
  /** Called with the new client once it exists. */
  onCreated: (client: { id: string }) => void;
  /** Leaves the form without creating anything. No button when there is none. */
  onCancel?: () => void;
}) {
  const addClient = useAddClientToPlatform(platformId ?? "");
  const createClient = useCreateClient();
  // The one that makes the client: in the platform, or standalone.
  const creating = platformId ? addClient : createClient;
  const queryClient = useQueryClient();
  // From pressing Create until the platform has loaded with the new client.
  const [saving, setSaving] = useState(false);
  // What went wrong in "Auto create", which makes several clients.
  const [autoError, setAutoError] = useState<string | null>(null);
  const [appType, setAppType] = useState<AppType>("api");
  const [purposeTag, setPurposeTag] = useState("api");
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  // The stack the application is implemented in (ADR-0060 decision 6), sent
  // with the client so the setup guide matches it.
  const [stack, setStack] = useState<string | null>(null);
  const [description, setDescription] = useState("");
  // A SPIFFE workload is the first answer, as in the design.
  const [authMethod, setAuthMethod] = useState<AuthMethod>("spiffe");

  const purpose = PURPOSES.find((p) => p.value === purposeTag) ?? PURPOSES[0];
  // An SPA holds no credential, so only services are asked where they run.
  const needsCredential = appType !== "spa";

  // A type that does not work for the new purpose gives way to the first one
  // that does; a type that still works stays.
  function pickPurpose(value: string) {
    const next = PURPOSES.find((p) => p.value === value);
    if (!next) return;
    setPurposeTag(next.value);
    if (!next.types.includes(appType)) setAppType(next.types[0]);
  }

  // In a platform, a client left without a name is named after the platform and
  // its type ("Apm API"). A standalone client has nothing to take a name from,
  // so it needs one. The Client ID is derived from the name. Both are shown in
  // the fields.
  const autoName = platformName
    ? `${platformName} ${APP_TYPE_NAME_SUFFIX[appType]}`
    : "";
  const effectiveName = name.trim() || autoName;
  const autoSlug = slugify(effectiveName);

  // Creates the client. The Client ID is left out when empty, so the registry
  // derives it from the name, the same way as the field shows. An SPA has no
  // credential, so no authMethod is sent for it.
  async function create() {
    if (!effectiveName) return;
    setSaving(true);
    setAutoError(null);
    try {
      const request = {
        name: effectiveName,
        slug: slug.trim() || undefined,
        description: description.trim() || undefined,
        appType,
        purposeTag,
        stack: stack ?? undefined,
        authMethod: needsCredential ? authMethod : undefined,
      };
      const created = platformId
        ? await addClient.mutateAsync(request)
        : await createClient.mutateAsync(request);
      // In a platform, wait for it to load again so the new client is in the
      // menu by the time it is selected.
      if (platformId) {
        await queryClient.invalidateQueries({
          queryKey: ["platforms", platformId],
        });
      }
      onCreated(created);
    } catch {
      // The error shows from the mutation state.
      setSaving(false);
    }
  }

  // Creates the clients of AUTO_CREATE_CLIENTS one after the other, then hands
  // over to the host (which offers the API still to be filled in). If one
  // fails, the ones before it exist: the platform is loaded again so they show
  // in the menu, and the message says which one failed.
  async function autoCreate() {
    if (!platformId || !platformName) return;
    setSaving(true);
    setAutoError(null);
    let first: ClientListItem | undefined;
    let failed: string | undefined;
    for (const spec of AUTO_CREATE_CLIENTS) {
      try {
        const created = await addClient.mutateAsync({
          name: `${platformName} ${spec.nameSuffix}`,
          appType: spec.appType,
          purposeTag: spec.purposeTag,
        });
        first ??= created;
      } catch (e) {
        failed = `Could not create the ${spec.label}: ${
          e instanceof Error ? e.message : "Something went wrong."
        }${first ? " The clients before it were created." : ""}`;
        break;
      }
    }
    await queryClient.invalidateQueries({
      queryKey: ["platforms", platformId],
    });
    if (failed) {
      setAutoError(failed);
      setSaving(false);
    } else {
      onAutoCreated?.();
    }
  }

  // The name and Client ID side by side, and the description under them.
  const nameFields = (
    <div className="new-client-name-fields">
      <div className="new-client-inputs">
        <Input
          label="Client name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={autoName || "Client name"}
        />
        <Input
          label="Client ID"
          value={slug}
          onChange={(e) => setSlug(e.target.value)}
          placeholder={autoSlug || "Auto-ID"}
        />
      </div>
      <TextArea
        label="Description"
        optional
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        placeholder="Briefly describe what this client does"
        rows={3}
      />
    </div>
  );

  return (
    <div className="client-object client-object-fill new-client-form">
      {/* The same heading as a client's name in the client object. */}
      <h3 className="bf-h3" style={{ margin: 0 }}>
        New client
      </h3>
      {suggestAutoCreate && (
        // The button sits at the right, centred on the message; the message
        // keeps room for it (see NewClientForm.css).
        <Message
          state="brand"
          header="Auto create"
          icon={faArrowRight}
          className="new-client-auto"
        >
          Unsure of what clients to create? “Auto Create” will get you started
          with the most common clients.
          <Button
            variant="basic"
            className="new-client-auto-button"
            onClick={saving ? undefined : autoCreate}
            state={saving ? "inactive" : undefined}
          >
            <Icon icon={faSparkles} marginRight />
            Auto create
          </Button>
        </Message>
      )}
      {/* A standalone client has no automatic name, so the name and the Client ID
          come first. In a platform they follow the type, as they have values of
          their own. */}
      {!platformId && nameFields}
      <Select
        label="Purpose"
        options={PURPOSES}
        value={purpose}
        onChange={(opt) => {
          const value = (opt as { value: string } | null)?.value;
          if (value) pickPurpose(value);
        }}
        style={{ maxWidth: 322 }}
      />

      <fieldset className="new-client-fieldset">
        <legend className="bf-label new-client-legend">Client type</legend>
        <div className="new-client-cards">
          {TYPE_CARDS.map((card) => {
            // A type the purpose does not work for stays visible but inactive.
            const works = purpose.types.includes(card.type);
            return (
              <RadioCard
                key={card.type}
                name="new-client-type"
                checked={appType === card.type}
                state={works ? undefined : "inactive"}
                onChange={() => works && setAppType(card.type)}
                label={
                  // An inactive type drops its colour for the neutral badge.
                  <Badge state={works ? clientTypeState(card.type) : "neutral"}>
                    {card.label}
                  </Badge>
                }
              >
                <span className="bfc-base-2">{card.description}</span>
              </RadioCard>
            );
          })}
        </div>
      </fieldset>

      {platformId && nameFields}

      <fieldset className="new-client-fieldset">
        <legend className="bf-label new-client-legend">
          Stack <span className="bf-small">(optional)</span>
        </legend>
        <p
          className="bf-small"
          style={{ color: "var(--bfc-base-c-2)", margin: "0 0 8px" }}
        >
          What the client's application is built with. React and .NET get a
          setup guide that matches.
        </p>
        <ClientStackPicker stack={stack} onPick={setStack} />
      </fieldset>

      {needsCredential && (
        <NewClientCredentialQuestion
          name="new-client-credential"
          value={authMethod}
          onChange={setAuthMethod}
        />
      )}

      {(autoError || creating.error) && (
        <Message state="alert" header="Could not create the client">
          {autoError ??
            (creating.error instanceof Error
              ? creating.error.message
              : "Something went wrong.")}
        </Message>
      )}

      <div
        className={`new-client-footer${onCancel ? "" : " new-client-footer-end"}`}
      >
        {onCancel && (
          <Button
            variant="flat"
            onClick={onCancel}
            state={saving ? "inactive" : undefined}
          >
            <Icon icon={faXmark} marginRight />
            Cancel process
          </Button>
        )}
        <Button
          variant="filled"
          onClick={saving || !effectiveName ? undefined : create}
          state={saving || !effectiveName ? "inactive" : undefined}
        >
          <Icon icon={faCheck} marginRight />
          {saving ? "Creating…" : "Create client"}
        </Button>
      </div>
    </div>
  );
}
